/**
 * User-web PTO listing URL, resolved from the admin site's domain at runtime:
 * staging admin → staging user web; dev admin (meey.dev) / localhost → dev
 * user web; anything else → production.
 */
const resolvePtoUrl = (): string => {
  const host = window.location.hostname
  if (host.includes('staging')) return 'https://meyreal-web-user-staging.meey.dev/pto'
  if (host.endsWith('meey.dev') || host === 'localhost' || host === '127.0.0.1')
    return 'https://meyreal-web-user.meey.dev/pto'
  return 'https://app.meyreal.io/pto'
}

/**
 * Fixed HTML body for the whitelist email (per marketing template, 8/7/2026).
 * The FE fills {{PROMO_CODE}} and {{PTO_URL}} (environment-specific CTA link);
 * the Subject only goes into the email subject, not the body.
 *
 * IMPORTANT: the BE sanitizes this body with sanitize-html before sending.
 * Its allowlist only keeps color, background-color, font and text CSS
 * properties plus the table attributes align/width/cellpadding/cellspacing —
 * every layout CSS property (padding, margin, border, border-radius, display,
 * line-height) is stripped. So ALL spacing here must come from table
 * cellpadding attributes and &nbsp; content. Rounded corners / dashed borders
 * can't survive; the CSS is still included so the design upgrades itself if
 * the BE allowlist ever adds those properties.
 */
const DISTRIBUTION_EMAIL_BODY_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
</head>

<body style="background-color:#f5f7fa;color:#333333;">
  <table width="100%" cellspacing="0" cellpadding="32" style="background-color:#f5f7fa;">
    <tr>
      <td align="center">

        <table width="600" cellspacing="0" cellpadding="0" style="background-color:#ffffff;border-radius:12px;">

          <!-- Banner -->
          <tr>
            <td>
              <img
                src="https://s3.hn-2.cloud.cmctelecom.vn/mey.network/2026/07/08/banneremail-1783482124068.png"
                alt="Mey Real Exclusive Investment Reward"
                width="600"
                style="display:block;"
              />
            </td>
          </tr>

          <!-- Content (cellpadding = section padding that survives sanitizing) -->
          <tr>
            <td>
              <table width="100%" cellspacing="0" cellpadding="40">
                <tr>
                  <td style="font-size:16px;color:#333333;">

                    <p style="font-size:16px;">Hello,</p>

                    <p style="font-size:16px;">Your exclusive promo code is ready.</p>

                    <p style="font-size:16px;">
                      Use it on your next <strong>Property Token Offering (PTO)</strong> and continue exploring tokenized real estate through Mey Real.
                    </p>

                    <!-- Promo Code -->
                    <table width="100%" cellspacing="0" cellpadding="8">
                      <tr>
                        <td align="center" style="font-size:15px;color:#666666;">Your Promo Code</td>
                      </tr>
                      <tr>
                        <td align="center">
                          <table align="center" cellspacing="0" cellpadding="18">
                            <tr>
                              <td
                                align="center"
                                style="background-color:#F5F8FF;border:2px dashed #2A5ADA;border-radius:10px;"
                              >
                                <span style="font-size:28px;font-weight:bold;color:#2A5ADA;">&nbsp;&nbsp;{{PROMO_CODE}}&nbsp;&nbsp;</span>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>

                    <p style="font-size:16px;">
                      Apply your code during checkout to enjoy your <strong>Exclusive Reward.</strong>
                    </p>

                    <p style="font-size:16px;">
                      Discover professionally managed tokenized real estate backed by real-world assets, with investment opportunities starting from just <strong>$50</strong>.
                    </p>

                    <p style="font-size:16px;">
                      Explore available opportunities and choose the property that best fits your investment goals.
                    </p>

                    <!-- CTA button: blue box drawn by td background-color + cellpadding -->
                    <table width="100%" cellspacing="0" cellpadding="10">
                      <tr>
                        <td align="center">
                          <table align="center" cellspacing="0" cellpadding="18">
                            <tr>
                              <td align="center" style="background-color:#2A5ADA;border-radius:14px;">
                                <a
                                  href="{{PTO_URL}}"
                                  target="_blank"
                                  style="display:block;width:100%;height:100%;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;background-color:#2A5ADA;"
                                >&nbsp;&nbsp;&nbsp;&nbsp;View&nbsp;Available&nbsp;PTOs&nbsp;&nbsp;&nbsp;&nbsp;</a>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>

                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color:#fafafa;">
              <table width="100%" cellspacing="0" cellpadding="32">
                <tr>
                  <td style="font-size:15px;color:#333333;">
                    <p style="font-size:15px;">Thank you for being part of Mey Real.</p>
                    <p style="font-size:15px;font-weight:bold;color:#111111;">Mey Real Team</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>

      </td>
    </tr>
  </table>
</body>
</html>`

export const buildDistributionEmailBody = (codeContent: string): string =>
  DISTRIBUTION_EMAIL_BODY_TEMPLATE.replace(/{{PROMO_CODE}}/g, codeContent).replace(
    /{{PTO_URL}}/g,
    resolvePtoUrl()
  )
